import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  Plus, 
  RefreshCw,
  FileText,
  Settings,
  Pencil
} from 'lucide-react'
import { clsx } from 'clsx'
import { 
  useDeal, 
  useHealthCard, 
  useHypotheses, 
  useEvidences,
  useDealPersons,
  useDemandLayers,
  useUpdateDemandLayer,
  useConfirmHypothesis, 
  useFalsifyHypothesis,
  useRefreshDiagnosis,
  useActionsByGrade,
  useActionsByHypothesis,
  useDealSignals,
  useObserveSignal,
  useUnobserveSignal,
  useAcceptAction,
  useCompleteAction,
  useRejectAction,
  useMarkNotApplicable,
  useTodoList,
  useUpdateScheduledDate
} from '@/hooks/useDeals'
import { evidenceService, hypothesisService } from '@/services/dealService'
import { 
  HealthCard, 
  HealthCardSkeleton, 
  EvidenceIngestionModal,
  EvidenceChainView,
  EvidenceEditModal,
  EditDealModal,
  HypothesisTree,
  HypothesisTreeSkeleton,
  ActionsByHypothesisPanel,
  ArtifactGenerator,
  PostMortemPanel,
  ChangeAlertPanel,
  OverviewActionPanel,
  OverviewSignalTracker,
  KeyPersonsCard,
  DemandLayersCard,
  DemandLayerEditModal,
  TodoListPanel
} from '@/components'
import StageProgressBar from '@/components/StageProgressBar'
import { useDealStore } from '@/stores/dealStore'
import { CollaborationPanel, MaskedViewBanner } from '@/components/collaboration'
import ExportButton from '@/components/ExportButton'
import UserMenu from '@/components/UserMenu'
import { getDealMembers } from '@/services/member'
import { DealRole } from '@/services/invitation'
import type { HypothesisDimension, ActionGrade, GradeActions, DemandLayerItem } from '@/types'

type TabType = 'overview' | 'evidence' | 'diagnosis' | 'actions' | 'artifacts' | 'postmortem'

export default function DealDetail() {
  const { dealId } = useParams<{ dealId: string }>()
  const [activeTab, setActiveTab] = useState<TabType>('overview')
  const [activeDimension, setActiveDimension] = useState<HypothesisDimension>('customer')
  const [isEvidenceModalOpen, setIsEvidenceModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isEditDealModalOpen, setIsEditDealModalOpen] = useState(false)
  const [editingEvidence, setEditingEvidence] = useState<any>(null)
  // 三层需求编辑状态
  const [editingDemandLayer, setEditingDemandLayer] = useState<DemandLayerItem | null>(null)
  
  // 用户角色状态
  const [currentUserRole, setCurrentUserRole] = useState<DealRole>('owner')
  const [isMaskedView, setIsMaskedView] = useState(false)
  
  // TASK-H8: 信号追踪改为原位展开，不再使用模态框
  
  // T9: 阶段感知状态 + T11: 变更预警状态
  const { 
    // T9: 阶段感知
    stageJudgement,
    stageCompletion,
    isStageLoading,
    fetchStageInfo,
    overrideStage,
    cancelStageOverride,
    // T11: 变更预警
    changeAlerts, 
    isChangeAlertsLoading, 
    fetchChangeAlerts, 
    dismissChangeAlert 
  } = useDealStore()
  
  // T9: 加载阶段信息 + T11: 加载变更预警 (BR-T11-22: 页面加载时并行获取)
  useEffect(() => {
    if (dealId) {
      // T9: 获取阶段判定和完成度
      fetchStageInfo(dealId)
      // T11: 获取变更预警
      fetchChangeAlerts(dealId)
    }
  }, [dealId, fetchStageInfo, fetchChangeAlerts])
  
  // 加载用户角色
  useEffect(() => {
    if (dealId) {
      getDealMembers(dealId).then(members => {
        // 获取当前用户在此 Deal 中的角色
        const storedUser = localStorage.getItem('user')
        if (storedUser) {
          const user = JSON.parse(storedUser)
          const myMembership = members.find(m => m.userId === user.id)
          if (myMembership) {
            setCurrentUserRole(myMembership.role)
            setIsMaskedView(myMembership.role === 'manager')
          }
        }
      }).catch(() => {
        // 如果加载失败，默认为 owner（保持兼容）
        setCurrentUserRole('owner')
      })
    }
  }, [dealId])

  // 数据加载
  const { data: deal, isLoading: isDealLoading } = useDeal(dealId)
  const { data: healthCard, isLoading: isHealthCardLoading } = useHealthCard(dealId)
  const { data: hypotheses, isLoading: isHypothesesLoading } = useHypotheses(dealId)
  const { data: evidencesData, isLoading: isEvidencesLoading, refetch: refetchEvidences } = useEvidences(dealId)
  const { data: personsData, isLoading: isPersonsLoading } = useDealPersons(dealId)
  const { data: demandLayersData, isLoading: isDemandLayersLoading } = useDemandLayers(dealId)
  const updateDemandLayerMutation = useUpdateDemandLayer()
  
  // 获取所有分级动作建议（不按诊断类型过滤，显示全部）- 用于概览页面
  const { data: actionsData, isLoading: isActionsLoading } = useActionsByGrade(dealId)
  
  // 获取按假设分组的动作 - 用于"建议动作"页面
  const { data: actionsByHypothesisData, isLoading: isActionsByHypothesisLoading, error: actionsByHypothesisError } = useActionsByHypothesis(dealId)
  
  // 待办事项列表（已采纳的动作）
  const { data: todoListData, isLoading: isTodoListLoading } = useTodoList(dealId)
  const updateScheduledDateMutation = useUpdateScheduledDate()
  
  // 调试输出
  console.log('[建议动作] dealId:', dealId)
  console.log('[建议动作] isLoading:', isActionsByHypothesisLoading)
  console.log('[建议动作] error:', actionsByHypothesisError)
  console.log('[建议动作] data:', actionsByHypothesisData)
  
  // 获取商机级别的信号数据
  const { data: signalsData, isLoading: isSignalsLoading } = useDealSignals(dealId)
  const observeSignalMutation = useObserveSignal()
  const unobserveSignalMutation = useUnobserveSignal()  // TASK-H8: 取消观测

  // Mutations
  const confirmHypothesis = useConfirmHypothesis()
  const falsifyHypothesis = useFalsifyHypothesis()
  const refreshDiagnosis = useRefreshDiagnosis()
  
  // 动作操作 mutations
  const acceptActionMutation = useAcceptAction()
  const completeActionMutation = useCompleteAction()
  const rejectActionMutation = useRejectAction()
  const markNotApplicableMutation = useMarkNotApplicable()

  const handleConfirmHypothesis = (hypothesisId: string) => {
    confirmHypothesis.mutate({ hypothesisId })
  }

  const handleFalsifyHypothesis = (hypothesisId: string) => {
    falsifyHypothesis.mutate({ hypothesisId })
  }

  const handleRefresh = () => {
    if (dealId) {
      refreshDiagnosis.mutate(dealId)
    }
  }

  // 获取假设关联的证据
  const handleFetchEvidences = async (hypothesisId: string) => {
    try {
      const response = await hypothesisService.getHypothesisEvidences(hypothesisId)
      return response.evidences || []
    } catch (error) {
      console.error('Failed to fetch hypothesis evidences:', error)
      return []
    }
  }

  // 获取假设关联的动作
  const handleFetchActions = async (hypothesisId: string) => {
    try {
      const response = await hypothesisService.getHypothesisActions(hypothesisId)
      // API 响应经过拦截器转换，snake_case → camelCase
      return response.actionsByGrade || { A: [], B: [], C: [] }
    } catch (error) {
      console.error('Failed to fetch hypothesis actions:', error)
      return { A: [], B: [], C: [] }
    }
  }

  // 模拟动作数据
  const mockActionsByGrade: Record<ActionGrade, GradeActions> = {
    A: {
      grade: 'A',
      label: '最佳路径',
      description: '完整验证，深度闭环',
      timeCostDays: 14,
      scenario: '项目早期（距签约>4周）',
      actions: [
        {
          id: '1',
          hypothesisId: '1',
          actionType: 'activity',
          actionGrade: 'A',
          depthLevel: 'Full',
          content: '通过Coach/TB引荐EB，安排高层汇报会，提供EB关注的ROI材料',
          timeCostDays: 14,
          timeCostDisplay: '2-3周',
          priority: 'P0',
          status: 'proposed',
          expectedSignals: [
            { strength: 'strong', description: 'EB参会+主动提问+承诺下一步' },
            { strength: 'medium', description: 'EB参会+被动听取+无明确反对' }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      ],
    },
    B: {
      grade: 'B',
      label: '快速验证',
      description: '关键信号快速验证',
      timeCostDays: 3,
      scenario: '项目中期（距签约2-4周）',
      actions: [
        {
          id: '2',
          hypothesisId: '1',
          actionType: 'question',
          actionGrade: 'B',
          depthLevel: 'Quick',
          content: '要求TB确认"预算审批流程中谁是最终决策人"',
          timeCostDays: 1,
          timeCostDisplay: '1天',
          priority: 'P1',
          status: 'proposed',
          expectedSignals: [
            { strength: 'strong', description: 'TB明确答复XX是最终决策人' },
            { strength: 'medium', description: 'TB模糊回应"领导会决定"' }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      ],
    },
    C: {
      grade: 'C',
      label: '止血动作',
      description: '不验证，只防输',
      timeCostDays: 0.5,
      scenario: '项目后期（距签约<2周）',
      actions: [
        {
          id: '3',
          hypothesisId: '1',
          actionType: 'touchpoint',
          actionGrade: 'C',
          depthLevel: 'Defense',
          content: '确认评审会议"是否必须EB本人到场"，若非必须则继续推进',
          timeCostDays: 0.2,
          timeCostDisplay: '4小时',
          priority: 'P1',
          status: 'proposed',
          warning: '⚠️ 此为止血动作，仍存在后期EB反对风险',
          expectedSignals: [
            { strength: 'strong', description: '明确"EB授权TB决策"的书面证据' },
            { strength: 'medium', description: 'TB口头确认"可以代表EB决策"' }
          ],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }
      ],
    },
  }

  const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: '概览', icon: FileText },
    { id: 'evidence', label: '证据链', icon: FileText },
    { id: 'diagnosis', label: '假设树', icon: FileText },
    { id: 'actions', label: '建议动作', icon: Settings },
    { id: 'artifacts', label: '输出件', icon: FileText },
    { id: 'postmortem', label: '复盘', icon: FileText },
  ]

  if (isDealLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* 脱敏视图横幅 */}
      {isMaskedView && <MaskedViewBanner />}
      
      {/* 顶部导航 */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold text-gray-900">
                    {deal?.name || '加载中...'}
                  </h1>
                  {deal && (
                    <button
                      onClick={() => setIsEditDealModalOpen(true)}
                      className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      title="编辑机会"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  {deal?.customerName} · {deal?.industry || '未知行业'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                disabled={refreshDiagnosis.isPending}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw className={clsx(
                  'w-4 h-4',
                  refreshDiagnosis.isPending && 'animate-spin'
                )} />
                刷新诊断
              </button>
              <ExportButton
                dealId={dealId || ''}
                dealName={deal?.name || ''}
              />
              <button
                onClick={() => setIsEvidenceModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                添加证据
              </button>
              <UserMenu />
            </div>
          </div>

          {/* Tab导航 */}
          <div className="flex items-center gap-1 mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* T11: 变更预警 Banner (BR-T11-20: 位于页面顶部, BR-T11-21: 有预警时显示) */}
        {changeAlerts.length > 0 && (
          <div className="mb-6">
            <ChangeAlertPanel
              alerts={changeAlerts}
              onDismiss={async (alertId, reason) => {
                await dismissChangeAlert(alertId, reason)
              }}
              onRefresh={() => dealId && fetchChangeAlerts(dealId)}
              isLoading={isChangeAlertsLoading}
            />
          </div>
        )}

        {/* 概览Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-3 gap-6">
            {/* 左侧列：待办事项 → 健康卡 → 建议动作 → 信号追踪 */}
            <div className="col-span-2 space-y-6">
              {/* 待办事项面板 */}
              <TodoListPanel
                todos={
                  todoListData?.todos?.map(item => ({
                    action: {
                      id: item.action.id,
                      hypothesisId: '',
                      actionType: 'activity' as const,
                      actionGrade: item.action.actionGrade,
                      depthLevel: 'Full' as const,
                      content: item.action.content,
                      status: item.action.status as 'proposed' | 'accepted' | 'done' | 'rejected',
                      priority: 'P1' as const,
                      scheduledDate: item.action.scheduledDate,
                      timeCostDisplay: item.action.timeCostDisplay,
                      hypothesisTitle: item.hypothesisTitle || item.action.hypothesisTitle,
                      createdAt: '',
                      updatedAt: '',
                    },
                    hypothesisTitle: item.hypothesisTitle,
                  })) || []
                }
                isLoading={isTodoListLoading}
                onComplete={async (actionId) => {
                  await completeActionMutation.mutateAsync(actionId)
                }}
                onUpdateScheduledDate={async (actionId, date) => {
                  await updateScheduledDateMutation.mutateAsync({ actionId, scheduledDate: date })
                }}
                onNavigateToActions={() => setActiveTab('actions')}
                maxDisplay={3}
              />

              {/* 健康卡 */}
              {isHealthCardLoading || isStageLoading ? (
                <HealthCardSkeleton />
              ) : healthCard ? (
                <HealthCard 
                  data={healthCard} 
                  // T9: 阶段感知 props (BR-T9-19~21)
                  stageJudgement={stageJudgement ?? undefined}
                  stageCompletion={stageCompletion ?? undefined}
                  hideStageProgress={true}
                  onStageOverride={async (stage, reason) => {
                    if (dealId) {
                      await overrideStage(dealId, stage, reason)
                    }
                  }}
                  onClearStageOverride={async () => {
                    if (dealId) {
                      await cancelStageOverride(dealId)
                    }
                  }}
                />
              ) : (
                <div className="p-6 bg-white rounded-2xl border border-gray-100 text-center">
                  <p className="text-gray-500">暂无诊断数据</p>
                </div>
              )}

              {/* 建议动作面板 */}
              <OverviewActionPanel
                diagnosisType={healthCard?.fatalGaps?.[0]?.title || '待诊断'}
                recommendedGrade={actionsData?.recommendedGrade || 'B'}
                actionsByGrade={actionsData?.actionsByGrade || mockActionsByGrade}
                onViewDetails={() => setActiveTab('actions')}
                isLoading={isActionsLoading}
                onAcceptAction={async (actionId, scheduledDate) => {
                  await acceptActionMutation.mutateAsync(actionId)
                  if (scheduledDate) {
                    await updateScheduledDateMutation.mutateAsync({ actionId, scheduledDate })
                  }
                }}
                onRejectAction={async (actionId, _reason) => {
                  await rejectActionMutation.mutateAsync(actionId)
                }}
                onRefresh={() => {
                  // React Query 会自动刷新
                }}
                onShowToast={(message, type) => {
                  console.log(`[Toast ${type}]: ${message}`)
                }}
              />

              {/* 信号追踪 - 使用真实 API 数据 */}
              <OverviewSignalTracker
                signals={
                  signalsData?.signals?.map(s => ({
                    id: s.id,
                    strength: (s.strength?.charAt(0).toUpperCase() + s.strength?.slice(1).toLowerCase()) as 'Strong' | 'Medium' | 'Weak',
                    description: s.description,
                    observed: s.observed,
                    observedAt: s.observedAt,
                    // 关联信息
                    actionId: s.actionId,
                    actionContent: s.actionContent,
                    hypothesisId: s.hypothesisId,
                    hypothesisCategory: s.hypothesisCategory,
                    hypothesisName: s.hypothesisName,
                  })) || []
                }
                onObserve={async (signalId) => {
                  await observeSignalMutation.mutateAsync({ signalId })
                }}
                onUnobserve={async (signalId) => {
                  await unobserveSignalMutation.mutateAsync({ signalId })
                }}
                onViewDetails={() => setActiveTab('actions')}
                isLoading={isSignalsLoading}
              />
            </div>

            {/* 右侧列：阶段进度 + 关键人 + 三层需求 + 协作面板 */}
            <div className="space-y-4">
              {/* 阶段进度（紧凑卡片） */}
              {stageJudgement && (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <StageProgressBar
                    stageJudgement={stageJudgement}
                    stageCompletion={stageCompletion ?? undefined}
                    onOverrideClick={() => {/* TODO: 打开覆盖弹窗 */}}
                  />
                </div>
              )}
              
              {/* 关键人卡片 */}
              <KeyPersonsCard
                persons={personsData || []}
                isLoading={isPersonsLoading}
                maxDisplay={5}
              />
              
              {/* 三层需求卡片 */}
              <DemandLayersCard
                layers={demandLayersData?.layers || []}
                isLoading={isDemandLayersLoading}
                onEdit={(layerType) => {
                  const layer = demandLayersData?.layers.find(l => l.layer === layerType)
                  if (layer) {
                    setEditingDemandLayer(layer)
                  }
                }}
                onViewDetails={() => setActiveTab('evidence')}
              />
              
              {/* 协作面板 */}
              <CollaborationPanel
                dealId={dealId || ''}
                dealName={deal?.name || ''}
                currentUserRole={currentUserRole}
              />
            </div>
          </div>
        )}

        {/* 假设树Tab */}
        {activeTab === 'diagnosis' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            {isHypothesesLoading ? (
              <HypothesisTreeSkeleton />
            ) : hypotheses?.dimensions ? (
              <HypothesisTree
                dimensions={hypotheses.dimensions}
                activeDimension={activeDimension}
                onDimensionChange={setActiveDimension}
                onConfirm={handleConfirmHypothesis}
                onFalsify={handleFalsifyHypothesis}
                onFetchEvidences={handleFetchEvidences}
                onFetchActions={handleFetchActions}
              />
            ) : (
              <div className="text-center py-12 text-gray-400">
                <p>暂无假设数据，请先添加证据</p>
              </div>
            )}
          </div>
        )}

        {/* 动作建议Tab - 按风险项分组展示 */}
        {activeTab === 'actions' && (
          <ActionsByHypothesisPanel
            hypotheses={actionsByHypothesisData?.hypotheses || []}
            totalTimeCostDays={actionsByHypothesisData?.totalTimeCostDays || 0}
            recommendedGrade={actionsByHypothesisData?.recommendedGrade || "B"}
            isLoading={isActionsByHypothesisLoading}
            dealId={dealId || ''}
            dealName={deal?.name || ''}
            // ============================================
            // 闭环回调：支持弹窗和后续操作
            // ============================================
            onAcceptAction={async (actionId, scheduledDate) => {
              // 先采纳
              await acceptActionMutation.mutateAsync(actionId)
              // 如果有设置日期，再更新日期
              if (scheduledDate) {
                await updateScheduledDateMutation.mutateAsync({ actionId, scheduledDate })
              }
            }}
            onCompleteAction={async (actionId) => {
              // 调用 mutateAsync 获取返回值（包含待确认信号）
              const result = await completeActionMutation.mutateAsync(actionId)
              // 返回结果供弹窗使用
              return {
                actionId,
                pendingSignals: result?.pendingSignals?.map(s => ({
                  id: s.id,
                  strength: (s.strength?.toLowerCase() || 'medium') as 'strong' | 'medium' | 'weak',
                  description: s.description,
                  requiresConfirmation: s.requiresConfirmation ?? true
                })) || [],
                requiresSignalConfirmation: result?.requiresSignalConfirmation || false,
                message: result?.message
              }
            }}
            onRejectAction={async (actionId, _reason) => {
              // TODO: 后端API支持reason参数后取消下划线
              await rejectActionMutation.mutateAsync(actionId)
            }}
            onMarkNotApplicable={async (actionId) => {
              await markNotApplicableMutation.mutateAsync(actionId)
            }}
            // 刷新回调
            onRefresh={() => {
              // 刷新动作数据
              if (dealId) {
                // 触发 react-query 重新获取数据
                // 由于使用的是 useActionsByHypothesis hook，修改 queryKey 会自动刷新
                // 这里通过 invalidateQueries 实现
              }
            }}
            // Toast 回调（TODO: 集成全局 Toast）
            onShowToast={(message, type) => {
              console.log(`[Toast ${type}]: ${message}`)
              // TODO: 集成全局 Toast 组件
            }}
            // TASK-H8: 信号追踪原位展开（替代模态框）
            onObserveSignal={async (signalId, evidenceId, notes) => {
              const result = await observeSignalMutation.mutateAsync({ signalId, evidenceId, notes })
              // 返回假设更新信息供弹窗使用
              return {
                hypothesisUpdated: result?.hypothesisUpdated || false,
                hypothesisUpdate: result?.hypothesisUpdate ? {
                  hypothesisId: result.hypothesisUpdate.hypothesisId,
                  oldStatus: result.hypothesisUpdate.oldStatus,
                  newStatus: result.hypothesisUpdate.newStatus
                } : undefined
              }
            }}
            onUnobserveSignal={async (signalId) => {
              await unobserveSignalMutation.mutateAsync({ signalId })
            }}
            onFetchEvidences={async (hypothesisId) => {
              const response = await hypothesisService.getHypothesisEvidences(hypothesisId)
              return (response.evidences || []).map((e: any) => ({
                id: e.id,
                summary: e.contentSummary || e.content?.substring(0, 100) || '证据',
                sourceType: e.sourceType,
                occurredAt: e.occurredAt,
              }))
            }}
            getEvidenceSummary={(evidenceId) => {
              const evidence = evidencesData?.items?.find((e: any) => e.id === evidenceId)
              return evidence?.contentSummary || evidence?.content?.substring(0, 50)
            }}
          />
        )}

        {/* 证据链Tab */}
        {activeTab === 'evidence' && (
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            {isEvidencesLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 text-primary-600 animate-spin" />
                <p className="text-gray-500">加载证据中...</p>
              </div>
            ) : (
              <EvidenceChainView
                evidences={evidencesData?.items || []}
                onEditEvidence={(evidenceId) => {
                  const evidence = evidencesData?.items?.find((e: any) => e.id === evidenceId)
                  if (evidence) {
                    setEditingEvidence(evidence)
                    setIsEditModalOpen(true)
                  }
                }}
              />
            )}
          </div>
        )}

        {/* 输出件Tab */}
        {activeTab === 'artifacts' && (
          <ArtifactGenerator
            dealId={dealId || ''}
            dealName={deal?.name || ''}
          />
        )}

        {/* 复盘Tab */}
        {activeTab === 'postmortem' && (
          <PostMortemPanel
            dealId={dealId || ''}
            dealName={deal?.name || ''}
          />
        )}
      </main>

      {/* 证据录入弹窗 */}
      <EvidenceIngestionModal
        isOpen={isEvidenceModalOpen}
        onClose={() => setIsEvidenceModalOpen(false)}
        dealId={dealId || ''}
        dealName={deal?.name || ''}
      />

      {/* 证据编辑弹窗 */}
      <EvidenceEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false)
          setEditingEvidence(null)
        }}
        evidence={editingEvidence}
        onSave={async (evidenceId, updates) => {
          // 调用API更新证据
          await evidenceService.updateEvidenceTags(evidenceId, {
            extractedTags: {
              persons: updates.persons,
              facts: updates.facts,
              riskFlags: updates.riskFlags
            }
          })
          
          if (updates.contentSummary) {
            await evidenceService.updateEvidenceSummary(evidenceId, updates.contentSummary)
          }
          
          // 刷新证据列表
          refetchEvidences()
        }}
      />

      {/* TASK-H8: 信号追踪改为原位展开，不再使用模态框 */}

      {/* 编辑机会弹窗 */}
      <EditDealModal
        isOpen={isEditDealModalOpen}
        onClose={() => setIsEditDealModalOpen(false)}
        deal={deal}
      />

      {/* 三层需求编辑弹窗 */}
      <DemandLayerEditModal
        isOpen={!!editingDemandLayer}
        onClose={() => setEditingDemandLayer(null)}
        layer={editingDemandLayer}
        onSave={async (layerType, data) => {
          if (!dealId) return
          await updateDemandLayerMutation.mutateAsync({ dealId, layer: layerType, data })
        }}
        isSaving={updateDemandLayerMutation.isPending}
      />
    </div>
  )
}
